


import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import datepicker from 'js-datepicker';

import CaptchaComponent from '@/shared/components/Captcha.vue';
import ButtonComponent from '@/shared/components/Button.vue';
import SelectOptionsComponent from '@/shared/components/SelectOptions.vue';
import InputTextComponent from '@/shared/components/InputText.vue';
import CheckComponent from '@/shared/components/Check.vue';
import Countries from '../Countries.vue';
import Identifications from '../Identifications.vue';
import FormError from '../FormError.vue';
import InputTextSection from '../InputTextSection.vue';
import SelectOptionsSection from '../SelectOptionsSection.vue';

import FormService from '@/shared/services/form-service';
import StudentService from '@/shared/services/student-service';
import HttpService from '@/shared/services/http-service';

import { Option } from '@/shared/models/view/option-viewmodel';
import { Country } from '@/shared/models/view/country-viewmodel';
import { Identification } from '@/shared/models/view/identification-viewmodel';
import { IConfig } from '../../models/config-viewmodel';

import { dateToIso, isoToDate } from '@/shared/filters/dates';

declare global {
    interface Window { grecaptcha: any; }
}

@Component({
    components: {
        ButtonComponent,
        SelectOptionsComponent,
        InputTextComponent,
        CheckComponent,
        Countries,
        Identifications,
        CaptchaComponent,
        FormError,
        InputTextSection,
        SelectOptionsSection
    },
    computed: {
        ...mapState(['form', 'preform', 'errors', 'formErrors', 'countries', 'offerings', 'identifications', 'config', 'submit']),
        ...mapGetters(['getTotalErrors']),
        ...mapGetters('root', ['label'])
    }
})
export default class StudyInformationViewComponent extends Vue {
    public loading = true;
    private labelModule = 'SelfServiceVUnetID';
    public countries!: Country[];
    public identifications!: Identification[];
    public form!: any;
    public preform!: any;
    public config!: IConfig;

    private fields = [
        {name: 'lastName'},
        {name: 'firstNames'},
        {name: 'initials'},
        {name: 'callName'},
        {name: 'gender'},
        {name: 'dateOfBirth'},
        {name: 'countryOfBirthIsoCode'},
        {name: 'placeOfBirth'},
        {name: 'nationalityIsoCode'},
        {name: 'identificationType'},
        {name: 'identificationNumber'},
        {name: 'street'},
        {name: 'houseNumber'},
        {name: 'postalCode', type: 'ZIPCODE', option: 'NL'},
        {name: 'city'},
        {name: 'countryIsoCode'},
        {name: 'emailAddress', type: 'EMAIL'},
        {name: 'verifiedEmailAddress', type: 'EMAIL_VALIDATE', option: 'emailAddress'},
        {name: 'phoneNumberCountryIsoCode'},
        {name: 'phoneNumber'}

        // TODO Validate Captcha
    ];

    private compare(a: any, b: any, key: string): number {
        if (a[key] < b[key])
            return -1;
        if (a[key] > b[key])
            return 1;
        return 0;
    }

    get countriesAsOptions(): Option[] {
        return this.countries ? this.countries.map((country: Country) => new Option(country.name, country.isoCode)) : [];
    }

    get nationalitiesAsOptions(): Option[] {
        return this.countries ? this.countries.map((country: Country) => {
            return new Option(country.nationality, country.isoCode)
        }).filter(a => a.name).sort((a, b) => this.compare(a, b, 'name')) : [];
    }

    get countryCodeAsOptions(): Option[] {
        return this.countries ? this.countries.map((country: Country) => new Option(`${country.name} (${country.countryCode})`, country.isoCode)) : [];
    }

    get identificationAsOptions(): Option[] {
        return this.identifications ? this.identifications.map((identification: Identification) => new Option(identification.name, identification.code)) : [];
    }

    mounted() {
        this.setPreFormValues();
        this.buildDatePicker();
        window.scrollTo(0, 0);
        
    }

    private buildDatePicker(): void {
        interface LooseObject {
            [key: string]: any
        }

        let dateOption: LooseObject  = {
            formatter: (input: any, date: any, instance: any) => {
                const value = isoToDate(date)
                input.value = value;
                this.setDateValue('dateOfBirth', input.value);
            }
        }

        if (this.config.language === 'nl') {
            dateOption.overlayPlaceholder = 'Kies een jaar';
            dateOption.overlayButton = 'Verstuur';
            dateOption.customDays = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
            dateOption.customMonths = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
            
        }
        
        const picker = datepicker('.vuw-date-picker', dateOption);
    }

  private setPreFormValues(): void {
        this.$store.commit('setFormValue', {name: 'registrationType', value: this.preform.registrationType});
        this.$store.commit('setFormValue', {name: 'externalOrganizationId', value: this.preform.universityId});
        this.$store.commit('setFormValue', {name: 'contractId', value: +this.preform.contractId});
        this.$store.commit('setFormValue', {name: 'programOfStudyId', value: +this.preform.studyId});
        this.$store.commit('setFormValue', {name: 'periodId', value: this.preform.period});
        this.$store.commit('setFormValue', {name: 'academicYear', value: +this.preform.academicYear});
    }

    private previousView(): void {
        this.$store.commit('root/clearErrors');
        this.$store.commit('resetFormErrors');
        this.$store.commit('setActiveView', 'StudyInformationView');
    }

    private setFormValue(name: string, value: any): void {
        this.$store.commit('setFormValue', {name, value});
    }

    private setDateValue(name: string, value: any): void {
        this.$store.commit('setFormValue', {name, value: dateToIso(value)});
    }

    private isoDate(value: string): string {
        return isoToDate(value);
    }

    private validateField(name: string, value: any, type: string | undefined, option?: any): void {
        let validation: {key: string}[] = FormService.validate(value, type, option);
        this.handleError(validation.map(validate => {
            return {key: validate.key, msg: validate.key}
        }), name);
    }

    private handleError(errors: {msg: string, key: string}[], name: string) {
        this.$store.commit('removeFormErrors', name);

        errors.forEach(({key, msg}) => {
            this.$store.commit('addFormErrors', {name, error: {key, msg}});
        })
    }

    private isValid(name: string): string {
        if (this.$store.getters.getFormErrors(name) && this.$store.getters.getFormErrors(name).length > 0) {
            return 'vuw-error';
        } return '';
    }

    private validateForm(): void {
        this.$store.commit('resetFormErrors');

        if (this.verifyCaptcha()) {
            this.fields.forEach(field => {
                // in the client side validator we only validate dutch postal codes
                // all other postal codes are validated server-side (as-is the dutch postal code as well)
                if (field.type === 'ZIPCODE') {
                    field.option = this.form['countryIsoCode'];
                }

                this.validateField(field.name, this.form[field.name], field.type || '', field.option || '');
            });

            this.postForm();
        }
    }

    private processServerErrors(errors: any) {
        Object.entries(errors).forEach((error: any) => {
            this.handleError(error[1].map((single: string) => {
                return {msg: single, key: ''}
            }), error[0])
        })
    }

    private verifyCaptcha(): boolean {
        if (window.grecaptcha.getResponse()) {
            this.$store.commit('setFormValue', { name: 'g-recaptcha-response', value: window.grecaptcha.getResponse() });
        } else {
            this.$store.commit('removeFormValue', 'g-recaptcha-response');
            this.$store.commit('addFormErrors', { name: "grecaptchaError", error: { key: "GoogleCaptchaError", msg: "Captcha is Invalid" } });
            return false;
        }

        return true;
    }

    private postForm(): void {
        if (!this.$store.getters.getTotalErrors) {
            this.$store.commit('root/clearErrors');
            this.$store.commit('setLoading', true);

            StudentService.postStudyDataByForm(this.config.language, this.form, HttpService).then(result => {
                this.$store.commit('setSubmit', true);
                this.$store.commit('setLoading', false);
            }).catch(error => {
                if (error.response.data.errors) {
                    this.processServerErrors(error.response.data.errors);
                } else {
                    console.log(error)
                    this.$store.commit('root/setErrors', [
                        this.config.language === 'nl' ? 
                            'Er gaat iets fout, probeer het later nog een keer' : 
                            'Something went wrong, please try again later'
                    ]);
                }
                //reset captcha
                window.grecaptcha.reset();

                this.$store.commit('setLoading', false);
            });
        }
    }
}
